import React from 'react';
import ModalFrame from '../../dashboard/components/product_item_components/ModalFrame';
import '../../dashboard/Dashboard.css';
import '../../business/Business.css';
import './OrderFilterSelectModal.css';
import './PaymentStatusSelectModal.css';
import { PaymentRecordStatusTypes } from '../../../types/PaymentRecordStatusTypes';
import { formatPaymentShortDate } from './dateUtils';

function parsePaidAmount(raw) {
  const n = parseFloat(String(raw ?? '').trim().replace(',', '.'));
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100) / 100;
}

function formatMoney(value) {
  const n = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(n)) return '0.00';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function isQrUrl(value) {
  return /^(https?:|data:)/i.test(String(value ?? '').trim());
}

export function qrImageUrl(source) {
  const candidates = [
    source?.qrUrl,
    source?.qr_url,
    source?.emvQrImg,
    source?.emv_qr_img,
    source?.qr,
    source?.qrUrl,
    source?.qr_url,
  ];
  for (const value of candidates) {
    const text = String(value ?? '').trim();
    if (isQrUrl(text)) return text;
  }
  return '';
}

function isGatewayPayment(payment) {
  return Boolean(payment?.invoiceId || payment?.invoice_id || payment?.qrUrl || payment?.qr_url);
}

function isPaidNonSystemPayment(payment) {
  return paymentStatusIdOf(payment) === PaymentRecordStatusTypes.PAID && isGatewayPayment(payment);
}

function leftoverAmount(totalPrice, currentDebt, payments) {
  const reserved = (payments || []).reduce((sum, payment) => {
    const statusId = paymentStatusIdOf(payment);
    if (
      statusId !== PaymentRecordStatusTypes.PAID &&
      statusId !== PaymentRecordStatusTypes.PENDING
    ) {
      return sum;
    }
    return sum + Math.abs(paymentAmountOf(payment));
  }, 0);
  const total = typeof totalPrice === 'number' ? totalPrice : parseFloat(totalPrice);
  if (Number.isFinite(total) && total > 0) {
    return Math.round(Math.max(0, total - reserved) * 100) / 100;
  }
  const debt = typeof currentDebt === 'number' ? currentDebt : parseFloat(currentDebt);
  if (Number.isFinite(debt) && debt > 0) {
    return Math.round(debt * 100) / 100;
  }
  return 0;
}

function paymentIdOf(payment) {
  return payment?.id ?? payment?._id ?? null;
}

function paymentStatusIdOf(payment) {
  return Number(payment?.statusId ?? payment?.status_id ?? PaymentRecordStatusTypes.PAID);
}

function paymentAmountOf(payment) {
  const n = typeof payment?.amount === 'number' ? payment.amount : parseFloat(payment?.amount);
  return Number.isFinite(n) ? n : 0;
}

function paymentCreatedAtOf(payment) {
  return payment?.createdAt ?? payment?.created_at;
}

function leftoverString(amount) {
  if (!Number.isFinite(amount) || amount <= 0) return '';
  return String(amount);
}

function PaymentStatusSelectModal({
  isOpen,
  payments = [],
  providers = [],
  currentDebt = 0,
  totalPrice = 0,
  submitting = false,
  error = null,
  onApply,
  onInitOtrade,
  onPollStatus,
  onCancelGateway,
  onClose,
}) {
  const leftoverNum = leftoverAmount(totalPrice, currentDebt, payments);

  const [view, setView] = React.useState('list');
  const [selectedPayment, setSelectedPayment] = React.useState(null);
  const [paidAmount, setPaidAmount] = React.useState('');
  const [chosenAmount, setChosenAmount] = React.useState(null);
  const [localError, setLocalError] = React.useState(null);

  React.useEffect(() => {
    if (!isOpen) return;
    setView('list');
    setSelectedPayment(null);
    setPaidAmount(leftoverString(leftoverNum));
    setChosenAmount(null);
    setLocalError(null);
  }, [isOpen, leftoverNum]);

  const selectedPaymentId = paymentIdOf(selectedPayment);
  const selectedStatusId = selectedPayment ? paymentStatusIdOf(selectedPayment) : null;

  React.useEffect(() => {
    if (!isOpen || view !== 'actions' || selectedPaymentId == null) return;
    if (selectedStatusId !== PaymentRecordStatusTypes.PENDING) return;
    if (typeof onPollStatus !== 'function') return;

    let stopped = false;
    const tick = async () => {
      const next = await onPollStatus(selectedPaymentId);
      if (stopped || !next) return;
      setSelectedPayment((prev) => ({
        ...(prev || {}),
        ...next,
        qrUrl: next.qrUrl || next.qr_url || prev?.qrUrl || prev?.qr_url || null,
      }));
    };

    tick();
    const timer = setInterval(tick, 2000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [isOpen, view, selectedPaymentId, selectedStatusId, onPollStatus]);

  if (!isOpen) return null;

  const displayError = localError || error;
  const canAdd = leftoverNum > 0 || payments.length === 0;

  const handleClose = () => {
    if (submitting) return;
    if (view === 'delete') {
      setView('actions');
      setLocalError(null);
      return;
    }
    if (view === 'actions' || view === 'method') {
      setView('list');
      setSelectedPayment(null);
      setLocalError(null);
      return;
    }
    onClose?.();
  };

  const handleSelectPayment = (payment) => {
    if (submitting) return;
    setSelectedPayment(payment);
    setView('actions');
    setLocalError(null);
  };

  const saveCash = async (paid) => {
    setLocalError(null);
    const ok = await onApply?.({ action: 'create', amount: paid });
    if (ok) {
      setView('list');
      setChosenAmount(null);
      setPaidAmount(leftoverString(Math.max(0, leftoverNum - paid)));
    }
  };

  const handleSave = async () => {
    const paid = parsePaidAmount(paidAmount);
    if (paid == null || paid <= 0) {
      setLocalError('Введите сумму оплаты');
      return;
    }
    if (paid > leftoverNum) {
      setLocalError('Сумма должна быть не больше остатка');
      return;
    }
    if (providers.length > 0) {
      setLocalError(null);
      setChosenAmount(paid);
      setView('method');
      return;
    }
    await saveCash(paid);
  };

  const handleChooseOtrade = async () => {
    const provider = providers[0];
    const providerId = provider?.id ?? provider?._id;
    if (providerId == null || chosenAmount == null) return;
    setLocalError(null);
    const result = await onInitOtrade?.({ providerId, amount: chosenAmount });
    if (result?.payment) {
      setSelectedPayment(result.payment);
      setChosenAmount(null);
      setView('actions');
      return;
    }
    setLocalError(result?.error || 'Не удалось создать оплату');
  };

  const handleToggleStatus = async () => {
    const paymentId = paymentIdOf(selectedPayment);
    if (paymentId == null) return;
    const statusId = paymentStatusIdOf(selectedPayment);
    const pendingGateway = isGatewayPayment(selectedPayment)
      && statusId === PaymentRecordStatusTypes.PENDING;
    setLocalError(null);
    if (pendingGateway) {
      const ok = await onCancelGateway?.(paymentId);
      if (ok) {
        setView('list');
        setSelectedPayment(null);
      }
      return;
    }
    const nextAction = statusId === PaymentRecordStatusTypes.CANCELED ? 'paid' : 'cancel';
    const ok = await onApply?.({ action: nextAction, paymentId });
    if (ok) {
      setView('list');
      setSelectedPayment(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (isPaidNonSystemPayment(selectedPayment)) {
      setLocalError('Нельзя удалить оплату, проведённую через платёжного провайдера');
      return;
    }
    const paymentId = paymentIdOf(selectedPayment);
    if (paymentId == null) return;
    setLocalError(null);
    const ok = await onApply?.({ action: 'delete', paymentId });
    if (ok) {
      setView('list');
      setSelectedPayment(null);
    }
  };

  if (view === 'delete' && selectedPayment) {
    const amountLabel = formatMoney(paymentAmountOf(selectedPayment));
    return (
      <ModalFrame title="Удалить оплату" onClose={handleClose}>
        <div className="main-page-editBody">
          {displayError ? <div className="payment-status-partialError">{displayError}</div> : null}
          <div className="main-page-confirmText">
            Удалить оплату на <b>{amountLabel} сом</b>?
          </div>
          <div className="main-page-editActions">
            <button
              type="button"
              className="main-page-editButton main-page-editButtonSecondary"
              onClick={handleClose}
              disabled={submitting}
            >
              Отмена
            </button>
            <button
              type="button"
              className="main-page-editButton main-page-editButtonDanger"
              onClick={handleConfirmDelete}
              disabled={submitting}
            >
              {submitting ? 'Удаление…' : 'Удалить'}
            </button>
          </div>
        </div>
      </ModalFrame>
    );
  }

  if (view === 'actions' && selectedPayment) {
    const statusId = paymentStatusIdOf(selectedPayment);
    const isCanceled = statusId === PaymentRecordStatusTypes.CANCELED;
    const deleteBlocked = isPaidNonSystemPayment(selectedPayment);
    const amountLabel = formatMoney(paymentAmountOf(selectedPayment));
    const dateLabel = formatPaymentShortDate(paymentCreatedAtOf(selectedPayment));
    const qrSrc = qrImageUrl(selectedPayment);
    return (
      <ModalFrame title={`${amountLabel} сом · ${dateLabel}`} onClose={handleClose}>
        <div className="payment-status-actions">
          {qrSrc ? <img className="payment-status-qr" src={qrSrc} alt="QR оплаты" /> : null}
          {displayError ? <div className="payment-status-partialError">{displayError}</div> : null}
          <div className="payment-status-actionsRow">
            <button
              type="button"
              className="main-page-editButton main-page-editButtonPrimary"
              onClick={handleToggleStatus}
              disabled={submitting}
            >
              {isCanceled ? 'Оплатить' : 'Отменить'}
            </button>
            {deleteBlocked ? null : (
              <button
                type="button"
                className="main-page-editButton main-page-editButtonDanger"
                onClick={() => {
                  setLocalError(null);
                  setView('delete');
                }}
                disabled={submitting}
              >
                Удалить
              </button>
            )}
          </div>
        </div>
      </ModalFrame>
    );
  }

  if (view === 'method') {
    return (
      <ModalFrame title="Способ оплаты" onClose={handleClose}>
        <div className="payment-status-list" role="listbox">
          {displayError ? <div className="payment-status-partialError">{displayError}</div> : null}
          <button
            type="button"
            className="main-page-modalItem payment-status-item"
            onClick={() => saveCash(chosenAmount)}
            disabled={submitting}
          >
            <span className="payment-status-itemAmount">Наличные</span>
          </button>
          <button
            type="button"
            className="main-page-modalItem payment-status-item"
            onClick={handleChooseOtrade}
            disabled={submitting}
          >
            <span className="payment-status-itemAmount">O!Trade</span>
          </button>
        </div>
      </ModalFrame>
    );
  }

  return (
    <ModalFrame title="Оплата" onClose={handleClose}>
      {payments.length > 0 ? (
        <div className="payment-status-list" role="listbox">
          {payments.map((payment) => {
            const id = paymentIdOf(payment);
            const statusId = paymentStatusIdOf(payment);
            const isCanceled = statusId === PaymentRecordStatusTypes.CANCELED;
            const isPending = statusId === PaymentRecordStatusTypes.PENDING;
            return (
              <button
                key={id}
                type="button"
                role="option"
                className={
                  isCanceled
                    ? 'main-page-modalItem payment-status-item payment-status-item--canceled'
                    : 'main-page-modalItem payment-status-item'
                }
                aria-selected={false}
                onClick={() => handleSelectPayment(payment)}
                disabled={submitting}
              >
                <span className="payment-status-itemAmount">
                  {formatMoney(paymentAmountOf(payment))} сом
                </span>
                <span className="payment-status-itemDate">
                  {formatPaymentShortDate(paymentCreatedAtOf(payment))}
                  {isPending ? ` · ${PaymentRecordStatusTypes.labels[PaymentRecordStatusTypes.PENDING]}` : ''}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
      {canAdd ? (
        <div className="payment-status-partial">
          <label className="payment-status-partialLabel" htmlFor="payment-status-paid-amount">
            Сумма
            <span className="payment-status-partialHint">
              Остаток {formatMoney(leftoverNum)} сом
            </span>
          </label>
          <input
            id="payment-status-paid-amount"
            className="shop-create-input"
            type="text"
            inputMode="decimal"
            placeholder="Сумма оплаты"
            value={paidAmount}
            onFocus={() => {
              setPaidAmount('');
              setLocalError(null);
            }}
            onChange={(e) => {
              setPaidAmount(e.target.value);
              setLocalError(null);
            }}
            disabled={submitting}
          />
          {displayError ? <div className="payment-status-partialError">{displayError}</div> : null}
          <div className="order-filter-select-actions">
            <button
              type="button"
              className="main-page-editButton main-page-editButtonPrimary"
              onClick={handleSave}
              disabled={submitting}
            >
              {submitting ? 'Сохранение…' : 'Оплатить'}
            </button>
          </div>
        </div>
      ) : displayError ? (
        <div className="payment-status-partial">
          <div className="payment-status-partialError">{displayError}</div>
        </div>
      ) : null}
    </ModalFrame>
  );
}

export default PaymentStatusSelectModal;
